import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal } from './common/Modal';
import { MdChevronRight, MdHome, MdSearch } from 'react-icons/md';
import { api } from '../utils/api';
import { TextInput } from './common/TextInput';

type EbayCategoryTreeData = {
  applicableMarketplaceIds: string[];
  categoryTreeId: string;
  categoryTreeVersion: string;
  rootCategoryNode: CategoryNode;
}

type CategoryNode = {
  category: {
    categoryId: string;
    categoryName: string;
  };
  categoryTreeNodeLevel: number;
  childCategoryTreeNodes?: CategoryNode[];
  leafCategoryTreeNode: boolean;
  parentCategoryTreeNodeHref: string;
}

interface CategorySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (category: CategoryNode) => void;
  shopId: string;
  lang: string;
}

export function CategorySelectionModal({
  isOpen,
  onClose,
  onSelect,
  shopId,
  lang,
}: CategorySelectionModalProps) {
  const [currentPath, setCurrentPath] = useState<CategoryNode[]>([]);
  const [currentNode, setCurrentNode] = useState<CategoryNode | null>(null);
  const [categoryTreeData, setCategoryTreeData] = useState<EbayCategoryTreeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CategoryNode[][]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const categoryTreeUrl = `/ebay/category_tree?shop_id=${encodeURIComponent(shopId)}&lang=${encodeURIComponent(lang)}`;

  // Fetch category tree data when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchCategoryTree = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await api.get(categoryTreeUrl);
          const data = await response.json();
          setCategoryTreeData(data);
          setCurrentPath([]);
          setCurrentNode(data.rootCategoryNode);
        } catch (err) {
          console.error('Error fetching category tree:', err);
          setError('Failed to load category tree. Please try again.');
        } finally {
          setIsLoading(false);
        }
      };

      fetchCategoryTree();
    }
  }, [isOpen, categoryTreeUrl]);

  // Helper function to normalize text for search (remove accents and convert to lowercase)
  const normalizeText = useCallback((text: string): string => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .toLowerCase();
  }, []);

  // Search function to find categories by name
  const searchCategories = useCallback((
    query: string,
    node: CategoryNode,
    currentPath: CategoryNode[]
  ): CategoryNode[][] => {
    const normalizedQuery = normalizeText(query);
    const results: CategoryNode[][] = [];

    // Check if current node matches
    if (normalizeText(node.category.categoryName).includes(normalizedQuery)) {
      results.push([
        ...currentPath,
        node
      ]);
      return results;
    }

    // Recursively search child nodes
    if (node.childCategoryTreeNodes) {
      for (const child of node.childCategoryTreeNodes) {
        results.push(...searchCategories(query, child, [
          ...currentPath,
          node
        ]));
      }
    }

    return results;
  }, [normalizeText]);

  // Search categories when search query changes
  useEffect(() => {
    if (!categoryTreeData || !searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    
    // Use setTimeout to debounce the search
    const timeoutId = setTimeout(() => {
      const results = searchCategories(
        searchQuery.trim(),
        categoryTreeData.rootCategoryNode,
        []
      );
      setSearchResults(results);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, categoryTreeData, searchCategories]);

  const handleCategoryClick = useCallback((category: CategoryNode) => {
    if (category.leafCategoryTreeNode) {
      // This is a leaf category, select it
      onSelect(category);
      onClose();
    } else {
      // Navigate to this category's children
      setCurrentPath(prev => [...prev, category]);
      setCurrentNode(category);
    }
  }, [onSelect, onClose]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index === -1) {
      // Home breadcrumb - go to root
      setCurrentPath([]);
      setCurrentNode(categoryTreeData?.rootCategoryNode || null);
    } else {
      // Navigate to specific breadcrumb level
      const newPath = currentPath.slice(0, index + 1);
      setCurrentPath(newPath);
      
      // Find the node at this level
      let node: CategoryNode | null = categoryTreeData?.rootCategoryNode || null;
      for (let i = 0; i < newPath.length; i++) {
        const pathItem = newPath[i];
        const foundNode = node?.childCategoryTreeNodes?.find(
          child => child.category.categoryId === pathItem.category.categoryId
        );
        node = foundNode || null;
      }
      setCurrentNode(node);
    }
  }, [currentPath, categoryTreeData]);

  const handleClose = useCallback(() => {
    setCurrentPath([]);
    setCurrentNode(null);
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  }, [onClose]);

  // Navigate to a category from search results
  const navigateToCategory = useCallback((category: CategoryNode) => {
    if (!categoryTreeData) return;

    // Find the path to this category
    const findPath = (node: CategoryNode, targetId: string, currentPath: CategoryNode[] = []): CategoryNode[] | null => {
      if (node.category.categoryId === targetId) {
        return currentPath;
      }

      if (node.childCategoryTreeNodes) {
        for (const child of node.childCategoryTreeNodes) {
          const result = findPath(child, targetId, [
            ...currentPath,
            child
          ]);
            if (result) return result;
        }
      }

      return null;
    };

    const path = findPath(categoryTreeData.rootCategoryNode, category.category.categoryId);
    if (path) {
      setCurrentPath(path);
      setCurrentNode(category);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [categoryTreeData]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      title="Select Category"
      onClose={handleClose}
      width="large"
    >
      <div className="flex flex-col h-full">

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading categories...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  setIsLoading(true);
                  // Retry fetching
                  api.get(categoryTreeUrl)
                    .then(response => response.json())
                    .then(data => {
                      setCategoryTreeData(data);
                      setCurrentPath([]);
                      setCurrentNode(data.rootCategoryNode);
                    })
                    .catch(err => {
                      console.error('Error fetching category tree:', err);
                      setError('Failed to load category tree. Please try again.');
                    })
                    .finally(() => setIsLoading(false));
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {categoryTreeData && (
          <>
            {/* Search Input */}
            <div className="mb-4">
              <TextInput
                label="Search categories"
                placeholder="Type to search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                LeftAccessory={<MdSearch size={20} className="text-gray-400" />}
                size="md"
              />
            </div>

            {/* Search Results */}
            {searchQuery.trim() && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  {isSearching
                    ? 'Searching...'
                    : `Search Results (${searchResults.length})`}
                </h3>
                <div className="max-h-48 overflow-y-auto border rounded-md">
                  {isSearching && (
                    <div className="p-4 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      Searching...
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="divide-y">
                      {searchResults.map((breadcrumbResult) => {
                        console.log({ breadcrumbResult });
                        const lastCategory = breadcrumbResult[breadcrumbResult.length - 1];
                        return (
                          <button
                            key={lastCategory.category.categoryId}
                            onClick={() => onSelect(lastCategory)}
                            className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900">
                                {breadcrumbResult.map(category => category.category.categoryName).join(' > ')}
                              </span>
                            </div>
                          </button>
                      )})}
                    </div>
                  )}

                  {!isSearching && searchResults.length === 0 && (
                    <div className="p-4 text-center text-gray-500">
                      No categories found matching "{searchQuery}"
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 mb-4 p-2 bg-gray-50 rounded-md">
              <button
                onClick={() => handleBreadcrumbClick(-1)}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <MdHome size={16} />
                Home
              </button>
              
              {currentPath.map((item, index) => (
                <React.Fragment key={item.category.categoryId}>
                  <MdChevronRight size={16} className="text-gray-400" />
                  <button
                    onClick={() => handleBreadcrumbClick(index)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium truncate max-w-32"
                    title={item.category.categoryName}
                  >
                    {item.category.categoryName}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Category List */}
            <div className="flex-1 overflow-y-auto">
              {currentNode?.childCategoryTreeNodes && currentNode.childCategoryTreeNodes.length > 0 ? (
                <div className="space-y-1">
                  {currentNode.childCategoryTreeNodes.map((category) => (
                    <button
                      key={category.category.categoryId}
                      onClick={() => handleCategoryClick(category)}
                      className="w-full text-left p-3 hover:bg-gray-50 rounded-md transition-colors border border-transparent hover:border-gray-200"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          {category.category.categoryName}
                        </span>
                        {!category.leafCategoryTreeNode && (
                          <MdChevronRight size={20} className="text-gray-400" />
                        )}
                      </div>
                      {category.leafCategoryTreeNode && (
                        <div className="text-xs text-gray-500 mt-1">
                          Leaf category
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No subcategories available
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}